import { A365BackendModel as Model } from '../a365.backendmodel';
import { Metamodel } from '../base/model';
import { AdapterRegistry } from './adapter-registry';
import { NamedAdapter } from './named.adapter';
import { Table } from '../table/table';
import { ModelConstants } from '../model-constants';
import { IColumn, ITableDataSource, IFormatInfo, IStorageDescriptor, ITable, IOriginDataTypeName, IPartitionInfo, IAttributeReference } from '../table/interface';
import { convertKeyToLowerCamelCase, convertKeyToUpperCamelCase } from '../../shared/utils/string.util';
import { TreeNamedResAndNameUtils } from '../../shared/utils/tree-named-naming.util';
import { SharedModel } from '../sharedmodel';
import { IExecuteChangeActionType } from '../database/databaseEntity';
import { GlobalVariables } from '../../constants/globals';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(Table),
    adapts: {
        name: '*',
        properties: '*'
    }
})
/* eslint-disable @typescript-eslint/no-explicit-any */
export class TableAdapter extends NamedAdapter<Table, Model.TableResource> {
    /**
     * If publish or forArmTemplate is true, return payload used in executeChangeWithValidation; otherwise, return the resource
     */
    protected _toResource(table: Table, isPublish: boolean = false, forArmTemplate: boolean = false): { [key: string]: any } {
        const realName = this._correctTableName(table);

        if (!isPublish && !forArmTemplate) {
            const sourceContent = this._generatePureNewTableEntity(table);
            return {
                name: realName,
                properties: sourceContent,
                type: 'TABLE'
            };
        }

        // Generate payload for executeChangeWithValidation
        const payload = this._generatePayload(table, forArmTemplate ? 'CREATE' : undefined);

        return {
            name: realName,
            databaseName: table.parentInfo ? table.parentInfo.resourceName : undefined,
            payload
        };
    }

    protected _toModel(tableResource: Model.TableResource, table?: Table): Table {
        if (!table) {
            table = Table.MODEL.create<Table>();
        }

        // set basic info
        const tableData = this._convertToTable(tableResource);
        const tableContent = tableData.table;
        const parentName = tableContent.namespace ? tableContent.namespace.databaseName : undefined; // database name is case insensitive
        const parentInfo = this._getParentInfo(parentName);
        table.name = TreeNamedResAndNameUtils.correctName(parentName, tableResource.name); // different databases could have tables with the same name
        table.description = tableContent.description;
        table.parentInfo = parentInfo;
        table.content = tableContent;
        // store the original contnet
        table.rawContentInJson = JSON.stringify(tableResource.properties);
        table.mDEntity = {
            name: tableResource.name, // real name of the table
            objectVersion: tableContent.objectVersion,
            objectId: tableContent.objectId
        };

        if (tableData.origin?.instanceId) {
            table.originInstanceId = tableData.origin.instanceId;
        }

        return table;
    }

    private _convertToTable(tableResource: Model.TableResource): { table: ITable, origin: { type: string, instanceId: string } } {
        const rawContent = convertKeyToLowerCamelCase(tableResource.properties);
        const rawStorageDescriptor = rawContent.storageDescriptor;
        const storageDescriptor = this._convertStorageDescriptor(rawStorageDescriptor);
        const partitionInfo = this._convertPartitionInfo(rawContent.partitioning);

        // Handle properties
        const properties = rawContent.properties;
        const derivedModelEntityInfoInString = properties ? properties.derivedModelEntityInfo : undefined;
        const rawDerivedModelEntityInfo = convertKeyToLowerCamelCase(derivedModelEntityInfoInString ? JSON.parse(derivedModelEntityInfoInString) : undefined);
        const derivedModelEntityInfo = rawDerivedModelEntityInfo ? {
            entityDirectives: rawDerivedModelEntityInfo.entityDirectives
        } : undefined;

        const displayFolderInfoInString = properties ? properties.displayFolderInfo : undefined;
        const sqlAppendableInfo = properties && properties['sql:IsAppendOnly'] ? properties['sql:IsAppendOnly'] : undefined;

        let rawDisplayFolderInfo;
        try {
            // If the table is created through script, this value may be in wrong format.
            rawDisplayFolderInfo = convertKeyToLowerCamelCase(displayFolderInfoInString ? JSON.parse(displayFolderInfoInString) : undefined);
        } catch {
            // As the value only influences UX, we can catch the error to unblock further usage.
            rawDisplayFolderInfo = undefined;
        }

        const displayFolderInfo = rawDisplayFolderInfo ? {
            name: rawDisplayFolderInfo.name,
            colorCode: rawDisplayFolderInfo.colorCode
        } : undefined;

        const rawFormat = rawStorageDescriptor ? rawStorageDescriptor.format : undefined;
        const formatType = rawFormat && rawFormat.formatType ? rawFormat.formatType : undefined;

        const primaryKeys = ((properties.primaryKeys && properties.primaryKeys.split(',')) // This is the case where properties.primaryKeys is present
            || (derivedModelEntityInfo ? derivedModelEntityInfo.entityDirectives.primaryKey : undefined)) // This is the case for older databases, and in case there is a delta in oackage updates
            || []; // If neither is present resort to []

        const table: ITable = {
            name: rawContent.name ? rawContent.name : tableResource.name,
            description: rawContent.description ? rawContent.description : properties.description, // description hasn't been supported in backend. Store it in properties for temporal fix
            objectId: rawContent.objectId,
            namespace: {
                ...rawContent.namespace
            },
            storageDescriptor,
            properties: {
                derivedModelEntityInfo,
                displayFolderInfo,
                primaryKeys,
                'spark.sql.sources.provider': formatType,
                'sql:IsAppendOnly': sqlAppendableInfo
            },
            partitioning: partitionInfo
        };

        return { table, origin: rawContent.origin };
    }

    private _convertPartitionInfo(rawPartitionInfo: Model.IRawPartitionInfo): IPartitionInfo | undefined {
        if (rawPartitionInfo) {
            const partitionFunctionType = rawPartitionInfo.partitionFunctionType;
            const keys = rawPartitionInfo.keys;
            return { partitionFunctionType, keys };
        }
        return undefined;
    }

    private _convertStorageDescriptor(rawStorageDescriptor: Model.IRawStorageDescriptor): IStorageDescriptor {
        if (rawStorageDescriptor) {
            // Convert column
            const rawColumns = rawStorageDescriptor.columns ? rawStorageDescriptor.columns : [];
            const baseAttributeReferences = (rawStorageDescriptor.properties
                && rawStorageDescriptor.properties.derivedModelAttributeInfo)
                ? (JSON.parse(rawStorageDescriptor.properties.derivedModelAttributeInfo).attributeReferences || {})
                : {};
            const columns: IColumn[] = [];
            rawColumns.forEach((rawColumn: Model.IRawColumn) => {
                const convertedColumn = this._convertDatabaseColumn(rawColumn, baseAttributeReferences);
                columns.push(convertedColumn);
            });
            // Convert format
            const rawFormat = rawStorageDescriptor.format;
            const formatProperties = rawFormat ? rawFormat.properties : undefined;
            const format: IFormatInfo = rawFormat ? {
                formatType: rawFormat.formatType ? rawFormat.formatType : undefined,
                formatTypeSetToDatabaseDefault: formatProperties ? formatProperties.formatTypeSetToDatabaseDefault : false,
                inputFormat: rawFormat.inputFormat ? rawFormat.inputFormat : undefined,
                outputFormat: rawFormat.outputFormat ? rawFormat.outputFormat : undefined,
                serializeLib: rawFormat.serializeLib ? rawFormat.serializeLib : undefined,
                properties: {
                    // Compression will be included in Storagedescriptor.properties, not in format as the sql data query for tables does not support it
                    path: formatProperties ? formatProperties.path : undefined,
                    delimiter: formatProperties ? formatProperties.delimiter : undefined,
                    firstRowAsHeader: formatProperties ? formatProperties.firstRowAsHeader : undefined,
                    multiLine: formatProperties ? formatProperties.multiLine : undefined,
                    'serialization.format': formatProperties ? formatProperties['serialization.format'] : undefined,
                    escape: formatProperties ? formatProperties.escape : undefined,
                    quote: formatProperties ? formatProperties.quote : undefined
                }
            } : {
                formatType: undefined,
                inputFormat: undefined,
                outputFormat: undefined,
                serializeLib: undefined,
                properties: {
                    path: undefined,
                    delimiter: undefined,
                    firstRowAsHeader: undefined,
                    multiLine: undefined,
                    'serialization.format': undefined,
                    escape: undefined,
                    quote: undefined
                }
            };

            // Convert data source
            const rawDataSource = rawStorageDescriptor.source;
            const dataSource: ITableDataSource = rawDataSource ? {
                provider: rawDataSource.provider ? rawDataSource.provider : undefined,
                location: rawDataSource.location ? rawDataSource.location : undefined,
                linkedServiceName: rawDataSource.properties ? rawDataSource.properties.linkedServiceName : undefined,
                locationSetToDatabaseDefault: rawDataSource.properties ? rawDataSource.properties.locationSetToDatabaseDefault : false
            } : {
                provider: undefined,
                location: undefined
            };
            const storageDescriptorProperties = rawStorageDescriptor.properties ? {
                'textinputformat.record.delimiter': rawStorageDescriptor.properties['textinputformat.record.delimiter'],
                compression: rawStorageDescriptor.properties.compression
            } : {
                'textinputformat.record.delimiter': undefined,
                compression: undefined
            };

            return {
                columns,
                format,
                source: dataSource,
                properties: storageDescriptorProperties
            };
        }
        return undefined;
    }

    private _convertDatabaseColumn(rawColumn: Model.IRawColumn, baseAttributeReferences: { [attribute: string]: IAttributeReference }): IColumn {
        const name = rawColumn.name;
        const description = rawColumn.description;
        const rawOriginDataTypeName = rawColumn.originDataTypeName;

        const originDataTypeName: IOriginDataTypeName = rawOriginDataTypeName ? {
            typeName: rawOriginDataTypeName.typeName ? rawOriginDataTypeName.typeName : 'string',
            isComplexType: rawOriginDataTypeName.isComplexType ? rawOriginDataTypeName.isComplexType : false,
            isNullable: rawOriginDataTypeName.isNullable ? rawOriginDataTypeName.isNullable : false,
            length: rawOriginDataTypeName.length,
            precision: rawOriginDataTypeName.precision,
            scale: rawOriginDataTypeName.scale,
            properties: rawOriginDataTypeName.properties
        } : {
            typeName: 'string',
            isComplexType: false,
            isNullable: false
        };

        const column: IColumn = {
            name,
            description,
            originDataTypeName
        };

        if (baseAttributeReferences && baseAttributeReferences[column.name]) {
            column.baseAttributeReference = baseAttributeReferences[column.name];
        }

        return column;
    }

    private _getParentInfo(databaseName: string): SharedModel.IParentInfo {
        return {
            resourceType: ModelConstants.database,
            resourceName: databaseName
        };
    }

    private _generatePayload(table: Table, actionType?: IExecuteChangeActionType): { [key: string]: any } {
        actionType = actionType ? actionType : table.alterAction;

        // Check if the table is renamed. If it is renamed, we can't perform upsert, as upsert checks on the name. Pass as ALTER
        const renamed = table.mDEntity?.name !== table.content?.name;
        if (!renamed) {
            actionType = 'CREATE';
        }

        if (actionType) {
            const newTableEntity = this._generatePureNewTableEntity(table);
            const oldTableEntity = actionType !== 'CREATE' ? this._generatePureOldTableEntity(table) : null;
            const entity = {
                ActionType: actionType,
                OldEntity: oldTableEntity,
                NewEntity: newTableEntity,
                Source: {
                    Type: 'SPARK'
                }
            };

            return entity;
        }
        return null;
    }

    /**
     * Generate table entity which is used as newEntity in executeChange
     */
    private _generatePureNewTableEntity(table: Table): { [key: string]: any } {
        const originalContent = convertKeyToUpperCamelCase(table.rawContentInJson ? JSON.parse(table.rawContentInJson) : {});
        // Column items
        const storageDescriptor = table.content && table.content.storageDescriptor ? table.content.storageDescriptor : {};
        const rawColumns = storageDescriptor.columns ? storageDescriptor.columns : [];
        const columns = rawColumns.map((column) => this._getColumnItem(column));
        const baseAttributeReferences: { [attribute: string]: IAttributeReference } = this._getBaseAttributeRefrences(rawColumns);

        // Handle spark related setting in properties
        const derivedModelEntityInfo = table.content && table.content.properties ? table.content.properties.derivedModelEntityInfo : undefined;
        const displayFolderInfo = table.content && table.content.properties ? table.content.properties.displayFolderInfo : undefined;
        const primaryKeys = (table.content && table.content.properties && table.content.properties.primaryKeys) || [];
        const partitionInfo = table.content && table.content.partitioning ? table.content.partitioning : undefined;
        const sqlSourceProviderInfo = table.content && table.content.properties ? table.content.properties['spark.sql.sources.provider'] : undefined;
        const sqlAppendableInfo = table.content && table.content.properties ? table.content.properties['sql:IsAppendOnly'] : undefined;
        const properties: any = derivedModelEntityInfo ? {
            DerivedModelEntityInfo: JSON.stringify(derivedModelEntityInfo),
            Description: table.description
        } : {
            Description: table.description
        };
        if (displayFolderInfo) {
            properties.DisplayFolderInfo = JSON.stringify(displayFolderInfo);
        }

        properties.PrimaryKeys = primaryKeys.join(',');

        if (sqlSourceProviderInfo) {
            properties['spark.sql.sources.provider'] = sqlSourceProviderInfo;
        }

        properties['sql:IsAppendOnly'] = sqlAppendableInfo;

        if (originalContent.Properties) {
            Object.keys(originalContent.Properties).forEach((key) => {
                if (key.includes('Spark.') || key.match(/^x-ms-usageInfo$/i) || key.startsWith('Sql:')) {
                    const newKey: string = key.replace(/^\S/g, (x) => x.toLowerCase());
                    originalContent.Properties[newKey] = originalContent.Properties[key];
                    delete originalContent.Properties[key];
                }
            });
        }

        const format = storageDescriptor && storageDescriptor.format ? {
            InputFormat: storageDescriptor.format.inputFormat,
            OutputFormat: storageDescriptor.format.outputFormat,
            FormatType: storageDescriptor.format.formatType,
            SerializeLib: storageDescriptor.format.serializeLib,
            Properties: {
                ...storageDescriptor.format.properties,
                FormatTypeSetToDatabaseDefault: storageDescriptor.format.formatTypeSetToDatabaseDefault,
                header: storageDescriptor.format.properties.firstRowAsHeader // This value is same as firstRowAsHeader. SQL OD needs this value,
                // whereas dataflow and datasets would need the previous value. We can later find the correct way to handle this
            }
        } : undefined;

        const source = storageDescriptor && storageDescriptor.source ? {
            Provider: storageDescriptor.source.provider,
            Location: storageDescriptor.source.location,
            Properties: {
                LinkedServiceName: storageDescriptor.source.linkedServiceName,
                LocationSetToDatabaseDefault: storageDescriptor.source.locationSetToDatabaseDefault
            }
        } : undefined;

        const entityProperties = {
            ...originalContent,
            Namespace: {
                ...originalContent.Namespace,
                DatabaseName: table.parentInfo.resourceName
            },
            Name: table.content ? table.content.name : table.name,
            Description: table.description,
            EntityType: 'TABLE',
            TableType: 'EXTERNAL',
            Origin: {
                Type: 'SPARK'
            },
            StorageDescriptor: {
                ...originalContent.StorageDescriptor,
                Columns: columns ? columns : [],
                // Not using convertKeyToUpperCamelCase for Format as we don't want to capitalize the keys inside Format->Properties
                Format: format,
                Source: source,
                Properties: {
                    ...storageDescriptor.properties,
                    derivedModelAttributeInfo: JSON.stringify({ attributeReferences: baseAttributeReferences })
                },
                Compressed: false,
                IsStoredAsSubdirectories: false
            },
            Properties: {
                ...originalContent.Properties,
                ...properties
            },
            Retention: 0,
            Temporary: false,
            IsRewriteEnabled: false,
            Partitioning: partitionInfo ? {
                PartitionFunctionType: partitionInfo.partitionFunctionType,
                Keys: partitionInfo.keys
            } : undefined
        };

        if (table.originInstanceId) {
            (entityProperties.Origin as any).InstanceId = table.originInstanceId;
        }

        const coreProperties = {
            Name: table.content ? table.content.name : table.name,
            EntityType: 'TABLE'
        };

        const entity = GlobalVariables.useLakeDB2021Api ? {
            name: coreProperties.Name,
            type: coreProperties.EntityType,
            properties: { ...entityProperties }
        } : {
            ...coreProperties,
            ...entityProperties
        };

        return entity;
    }

    /**
     * Generate table entity which is used as oldEntity in executeChange
     * The entity would only contain necessary attribute instead of all
     */
    private _generatePureOldTableEntity(table: Table): { [key: string]: any } {
        const originalName = table.mDEntity ? table.mDEntity.name : undefined;
        const entityProperties = {
            Namespace: {
                DatabaseName: table.parentInfo.resourceName
            },
            Name: originalName ? originalName : (table.content ? table.content.name : table.name),
            EntityType: 'TABLE',
            TableType: 'EXTERNAL',
            Origin: {
                Type: 'SPARK'
            },
            ObjectId: table.mDEntity ? table.mDEntity.objectId : undefined,
            ObjectVersion: table.mDEntity ? table.mDEntity.objectVersion : undefined
        };

        const coreProperties = {
            Name: originalName ? originalName : (table.content ? table.content.name : table.name),
            EntityType: 'TABLE',
        };

        const entity = GlobalVariables.useLakeDB2021Api ? {
            name: coreProperties.Name,
            type: coreProperties.EntityType,
            properties: { ...entityProperties }
        } : {
            ...coreProperties,
            ...entityProperties
        };

        return entity;
    }

    private _getColumnItem(column: IColumn): { [key: string]: any } {
        const originDataTypeName = column.originDataTypeName;
        const originalDataTypeNameWithUpperCamelCaseKeys = convertKeyToUpperCamelCase(originDataTypeName);
        const columnWithUpperCamelCaseKeys = convertKeyToUpperCamelCase(column);

        return {
            ...columnWithUpperCamelCaseKeys,
            Name: column.name,
            OriginDataTypeName: {
                ...originalDataTypeNameWithUpperCamelCaseKeys,
                TypeName: originDataTypeName ? originDataTypeName.typeName : undefined,
                IsComplexType: originDataTypeName ? originDataTypeName.isComplexType : false,
                IsNullable: originDataTypeName ? originDataTypeName.isNullable : false,
                Length: originDataTypeName ? originDataTypeName.length : undefined,
                Precision: originDataTypeName ? originDataTypeName.precision : undefined,
                Scale: originDataTypeName ? originDataTypeName.scale : undefined,
                Properties: {
                    ...(originalDataTypeNameWithUpperCamelCaseKeys ? originalDataTypeNameWithUpperCamelCaseKeys.Properties : undefined),
                    HIVE_TYPE_STRING: originDataTypeName ? originDataTypeName.typeName : 'int'
                }
            },
            Description: column.description
        };
    }

    private _getBaseAttributeRefrences(columns: IColumn[]): { [attribute: string]: IAttributeReference } {
        const baseAttributeReferences: { [attribute: string]: IAttributeReference } = {};
        columns.forEach((column) => {
            if (column.baseAttributeReference) {
                baseAttributeReferences[column.name] = column.baseAttributeReference;
            }
        });

        return baseAttributeReferences;
    }

    private _correctTableName(tableNamed: Table): string {
        const nameDict = TreeNamedResAndNameUtils.extractNameAndParentName(tableNamed.name);
        return nameDict.name;
    }
}
